/**
 * TimelineForkModal.jsx - Choice modal for Timeline Fork aircraft
 *
 * Presents player with two mutually exclusive choices when the flight completes.
 * Each choice has different rewards/consequences.
 */

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";

export default function TimelineForkModal({
  isOpen,
  onClose,
  onChoice,
  choiceA,
  choiceB,
  message,
}) {
  const handleChoiceA = useCallback(() => {
    onChoice?.("A", choiceA);
    onClose?.();
  }, [onChoice, onClose, choiceA]);

  const handleChoiceB = useCallback(() => {
    onChoice?.("B", choiceB);
    onClose?.();
  }, [onChoice, onClose, choiceB]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 99998,
      }}
    >
      {/* Glitch effect background */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "repeating-linear-gradient(0deg, rgba(0,255,255,0.03) 0px, rgba(0,255,255,0.03) 1px, transparent 1px, transparent 2px)",
          pointerEvents: "none",
        }}
      />

      {/* Modal content */}
      <div
        style={{
          backgroundColor: "#0a0a0a",
          border: "1px solid #333",
          borderRadius: "8px",
          padding: "40px",
          maxWidth: "600px",
          width: "90%",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Title */}
        <h2
          style={{
            color: "#00ffff",
            fontSize: "24px",
            fontWeight: "bold",
            marginBottom: "10px",
            textShadow: "0 0 10px rgba(0,255,255,0.5)",
          }}
        >
          TIMELINE FORK
        </h2>

        {/* Message */}
        <p
          style={{
            color: "#a1a1a1",
            fontSize: "16px",
            marginBottom: "30px",
          }}
        >
          {message || "Choose your reality."}
        </p>

        {/* Choices */}
        <div
          style={{
            display: "flex",
            gap: "20px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Choice A */}
          <button
            onClick={handleChoiceA}
            style={{
              backgroundColor: "#1a1a1a",
              border: "2px solid #22c55e",
              borderRadius: "8px",
              padding: "20px 30px",
              cursor: "pointer",
              minWidth: "200px",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#052e16";
              e.currentTarget.style.boxShadow = "0 0 20px rgba(34,197,94,0.3)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#1a1a1a";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              style={{
                color: "#22c55e",
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {choiceA?.label || "Option A"}
            </div>
            <div style={{ color: "#737373", fontSize: "14px" }}>
              {choiceA?.revenueMultiplier && (
                <div>Revenue: {choiceA.revenueMultiplier}x</div>
              )}
              {choiceA?.reputationChange && (
                <div>
                  Reputation: {choiceA.reputationChange > 0 ? "+" : ""}
                  {choiceA.reputationChange}
                </div>
              )}
            </div>
          </button>

          {/* Choice B */}
          <button
            onClick={handleChoiceB}
            style={{
              backgroundColor: "#1a1a1a",
              border: "2px solid #3b82f6",
              borderRadius: "8px",
              padding: "20px 30px",
              cursor: "pointer",
              minWidth: "200px",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#0c1929";
              e.currentTarget.style.boxShadow = "0 0 20px rgba(59,130,246,0.3)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#1a1a1a";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              style={{
                color: "#3b82f6",
                fontSize: "18px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {choiceB?.label || "Option B"}
            </div>
            <div style={{ color: "#737373", fontSize: "14px" }}>
              {choiceB?.revenueMultiplier && (
                <div>Revenue: {choiceB.revenueMultiplier}x</div>
              )}
              {choiceB?.reputationChange && (
                <div>
                  Reputation: {choiceB.reputationChange > 0 ? "+" : ""}
                  {choiceB.reputationChange}
                </div>
              )}
            </div>
          </button>
        </div>

        {/* Warning */}
        <p
          style={{
            color: "#ef4444",
            fontSize: "12px",
            marginTop: "20px",
            opacity: 0.7,
          }}
        >
          This choice is permanent. The other timeline will collapse.
        </p>
      </div>
    </div>
  );
}

/**
 * Hook to manage Timeline Fork state
 */
export function useTimelineFork() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [choiceA, setChoiceA] = React.useState(null);
  const [choiceB, setChoiceB] = React.useState(null);
  const [message, setMessage] = React.useState("");
  const [onChoiceCallback, setOnChoiceCallback] = React.useState(null);

  const showFork = React.useCallback((config, callback) => {
    setChoiceA(config.choiceA);
    setChoiceB(config.choiceB);
    setMessage(config.message || "Choose your reality.");
    setOnChoiceCallback(() => callback);
    setIsOpen(true);
  }, []);

  const hideFork = React.useCallback(() => {
    setIsOpen(false);
    setChoiceA(null);
    setChoiceB(null);
    setMessage("");
    setOnChoiceCallback(null);
  }, []);

  const handleChoice = React.useCallback(
    (choice, data) => {
      if (onChoiceCallback) {
        onChoiceCallback(choice, data);
      }
    },
    [onChoiceCallback]
  );

  return {
    isOpen,
    choiceA,
    choiceB,
    message,
    showFork,
    hideFork,
    handleChoice,
  };
}
